import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';

// Per-system "Deres referanse" override (edited on the System card — see
// card-view-system-card.component.ts's `customerReference` field). Used by
// BillingComponent.buildInvoices() to split a customer's monthly bill into
// several invoices when their systems don't all share the same reference —
// e.g. two systems at two different cost centers under the same kundekort.
//
// Falls back to the kundekort's own `customerReference` (see kunde-kort-
// info-popup.ts) when a system has none of its own — most customers never
// touch this and get exactly one invoice, unchanged.
@Injectable({ providedIn: 'root' })
export class SystemReferenceService {

  constructor(private afs: AngularFirestore) { }

  // Keyed BOTH by the system_info doc id and by its own `systemId` field —
  // other parts of this codebase inconsistently use one or the other to
  // refer to "a system" (see GarantiomsetningService.getGarantiSystems's
  // dual-lookup comment), so callers can look up by whichever they have.
  async getCustomerReferenceBySystem(): Promise<{ [systemId: string]: string }> {
    const snap = await this.afs.collection('system_info').ref.get();
    const out: { [systemId: string]: string } = {};
    snap.docs.forEach(d => {
      const v = d.data() as any;
      const ref = v.customerReference ? String(v.customerReference).trim() : '';
      if (!ref) { return; }
      out[d.id] = ref;
      if (v.systemId) { out[v.systemId] = ref; }
    });
    return out;
  }

  // Per-GROUP "Deres referanse" (group dialog). A group with one gets an invoice
  // of its own for its members' purchases, even when another group of the same
  // customer carries the identical reference (Bane Nor: three groups on one
  // system, each invoiced separately). Keyed customerId -> groupId, because the
  // reconciliation identifies groups by their groupId slug, which is only
  // unique within a customer. Deleted groups are included: a group removed
  // mid-month still owns the purchases made before.
  async getGroupReferences(): Promise<{ [customerId: string]: { [groupId: string]: { ref: string, name: string } } }> {
    const snap = await this.afs.firestore.collection('groups').where('customerReference', '>', '').get();
    const out: { [customerId: string]: { [groupId: string]: { ref: string, name: string } } } = {};
    snap.docs.forEach(d => {
      const g = d.data() as any;
      const ref = String(g.customerReference || '').trim();
      if (!ref || !g.customerId || !g.groupId) { return; }
      (out[g.customerId] || (out[g.customerId] = {}))[g.groupId] = { ref, name: g.name || g.groupId };
    });
    return out;
  }
}
